import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMyDailyDirectClient, track } from "@mydailydirect/app-kit";
import {
  Badge,
  Button,
  Banner,
  FilterChip,
  ProductCard,
  StickyActionBar,
  EmptyState,
  ErrorState,
  SearchBar,
  Select,
  CatalogGridSkeleton,
  InteractiveLoader,
  MandiRateCardModal,
  SampleKitModal,
  RepeatOrderRail,
  calculateEffectiveTierPrice,
  MDD_SUPPORT_WHATSAPP_URL,
} from "@mydailydirect/ui";
import {
  ArrowUpDown,
  Package,
  Wheat,
  Droplets,
  Flame,
  Milk,
  Sparkles,
  UserCheck,
  Camera,
  MessageSquare,
  TrendingUp,
  FileText,
  ChefHat,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { SkuDTO } from "@mydailydirect/api-client";
import { useCart } from "../lib/cart";
import { useRetailerId } from "../lib/useRetailerId";
import { UploadShopProofModal } from "../components/UploadShopProofModal";

/**
 * Verification Policy Flag:
 * Set to true if retail catalog listings should be hidden completely until the shop is approved.
 * Currently false so retailers can explore inventory, while checkout remains strictly gated.
 */
export const BLOCK_LISTINGS_UNTIL_VERIFIED = false;

type SortOption = "popular" | "price-asc" | "price-desc";

function getCategoryIcon(category: string) {
  const lower = category.toLowerCase();
  if (lower.includes("staple") || lower.includes("grain") || lower.includes("rice") || lower.includes("flour")) {
    return Wheat;
  }
  if (lower.includes("oil") || lower.includes("ghee")) {
    return Droplets;
  }
  if (lower.includes("spice") || lower.includes("masala")) {
    return Flame;
  }
  if (lower.includes("dairy") || lower.includes("milk")) {
    return Milk;
  }
  return Package;
}

// Paused until ops solves dock and packing sample facility
const ENABLE_SAMPLE_KIT = false;

export interface BrowsePageProps {
  /** Optional override for standalone demo / design lab previews */
  skus?: SkuDTO[];
}

export function BrowsePage({ skus: propSkus }: BrowsePageProps = {}) {
  const navigate = useNavigate();
  const client = useMyDailyDirectClient();
  const cart = useCart();
  const queryClient = useQueryClient();
  const { retailer, isVerified, isApproved, hasShopProof, hasGstNumber, buyerName, buyerPhone } = useRetailerId();

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [sortBy, setSortBy] = useState<SortOption>("popular");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);
  const [isSampleKitModalOpen, setIsSampleKitModalOpen] = useState(false);
  const openCart = () => window.dispatchEvent(new CustomEvent("mdd:open-cart"));

  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    initialData: propSkus,
  });

  const resolvedSkus = propSkus ?? skusQuery.data;

  // Query previous orders to power 1-Tap Kitchen Reorder / "Buy Again" Staples rail
  const ordersQuery = useQuery({
    queryKey: ["orders", retailer?.id],
    queryFn: () => client.orders.list(),
    enabled: Boolean(retailer?.id),
  });

  const repeatItems = useMemo(() => {
    if (!ordersQuery.data || !retailer?.id || !skusQuery.data) return [];
    const myOrders = ordersQuery.data
      .filter((o) => o.retailerId === retailer.id)
      .sort((a, b) => b.id - a.id);
    if (myOrders.length === 0) return [];

    const map = new Map<number, { name: string; qty: number; pricePaise: number; photoUrl?: string }>();
    for (const order of myOrders.slice(0, 3)) {
      for (const line of order.lines) {
        if (!map.has(line.skuId)) {
          const sku = skusQuery.data.find((s) => s.id === line.skuId);
          if (sku && sku.active && sku.availableQty > 0) {
            map.set(line.skuId, {
              name: sku.name,
              qty: line.qty,
              pricePaise: sku.pricePaise,
              photoUrl: sku.photoUrls?.[0],
            });
          }
        }
      }
    }
    return Array.from(map.entries()).map(([skuId, data]) => ({
      id: skuId,
      name: data.name,
      qty: data.qty,
      pricePaise: data.pricePaise,
      photoUrl: data.photoUrl,
    }));
  }, [ordersQuery.data, retailer?.id, skusQuery.data]);

  // Dynamically extract categories with counts
  const categories = useMemo(() => {
    const activeItems = (resolvedSkus ?? []).filter(
      (s) => s.active && (!s.approvalStatus || s.approvalStatus === "APPROVED")
    );
    const map = new Map<string, number>();

    activeItems.forEach((s) => {
      if (s.category) {
        map.set(s.category, (map.get(s.category) ?? 0) + 1);
      }
    });

    const list = Array.from(map.entries()).map(([cat, count]) => ({
      label: cat,
      value: cat,
      count,
      icon: getCategoryIcon(cat),
    }));

    return [
      { label: "All Items", value: "ALL", count: activeItems.length, icon: Sparkles },
      ...list,
    ];
  }, [skusQuery.data]);

  // Filter and sort SKUs
  const visible = useMemo(() => {
    let items = (resolvedSkus ?? []).filter(
      (s) => s.active && (!s.approvalStatus || s.approvalStatus === "APPROVED")
    );

    if (selectedCategory !== "ALL") {
      items = items.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase());
    }

    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          (s.category && s.category.toLowerCase().includes(q))
      );
    }

    items = [...items].sort((a, b) => {
      if (sortBy === "price-asc") return a.pricePaise - b.pricePaise;
      if (sortBy === "price-desc") return b.pricePaise - a.pricePaise;
      return b.availableQty - a.availableQty;
    });

    return items;
  }, [skusQuery.data, search, selectedCategory, sortBy]);

  // Telemetry: track search queries and zero-result searches
  useEffect(() => {
    const q = search.trim();
    if (!q || q.length < 2) return;
    const timer = setTimeout(() => {
      track("search", {
        searchQuery: q,
        propsJson: {
          count: visible.length,
          category: selectedCategory,
        },
      });
    }, 750);
    return () => clearTimeout(timer);
  }, [search, visible.length, selectedCategory]);

  // Calculate Cart Total with Tiered Bulk Discounts
  const cartTotal = useMemo(() => {
    const byId = new Map((resolvedSkus ?? []).map((s) => [s.id, s]));
    return cart.lines.reduce((sum, l) => {
      const sku = byId.get(l.skuId);
      if (!sku) return sum;
      const tierCalc = calculateEffectiveTierPrice(sku, l.qty);
      return sum + tierCalc.totalPaise;
    }, 0);
  }, [cart.lines, skusQuery.data]);

  return (
    <div className="flex flex-col pb-6 bg-background">
      {/* Verification In Progress Banner (Refactored to Banner primitive) */}
      {!isVerified && (
        <Banner
          tone="warning"
          layout="strip"
          icon={Camera}
          className="mb-2"
          title={
            !hasShopProof || !hasGstNumber
              ? "Wholesale KYC: 15-char GSTIN & shop proof document required"
              : "Shop Verification: Documents submitted and pending review"
          }
          actions={
            <>
              <Button
                size="sm"
                variant={!hasShopProof || !hasGstNumber ? "primary" : "outline"}
                onClick={() => setIsUploadModalOpen(true)}
                className="h-7 px-3 text-xs font-bold"
              >
                <FileText className="h-3.5 w-3.5 mr-1" />
                {!hasShopProof || !hasGstNumber ? "Upload KYC Docs" : "Update Documents"}
              </Button>
              {hasShopProof && hasGstNumber && !isApproved && (
                <Badge variant="warning" size="sm">
                  Pending Approval
                </Badge>
              )}
            </>
          }
        >
          Catalog browsing enabled. Verification unlocks morning wholesale checkout.
        </Banner>
      )}

      {/* RM Pushed Cart Notification Banner */}
      {cart.pushedBy && (
        <Banner
          tone="info"
          icon={UserCheck}
          className="mb-2"
          title={`Cart prepared by ${cart.pushedBy} (${cart.totalItems} items)`}
          actions={
            <Button size="sm" variant="primary" className="h-7 text-xs px-2.5" onClick={openCart}>
              Review
            </Button>
          }
        >
          Your Relationship Manager assigned items for your morning replenishment.
        </Banner>
      )}

      {/* Yeswanthpur APMC Commodity Trend & Lowest Price Guarantee Bar */}
      <Banner
        tone="promo"
        layout="card"
        icon={TrendingUp}
        className="mb-3"
        title={
          <div className="flex items-center gap-2 flex-wrap">
            <span>Trusted Sourcing · Rigorous QC & Quality Tested</span>
            <Badge variant="success" size="sm" dot>
              100% Quality & Weight Verified
            </Badge>
          </div>
        }
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsRateCardOpen(true)}
              className="h-8 gap-1.5 font-bold text-xs"
            >
              <Sparkles className="h-3.5 w-3.5 text-tone-warning-text" />
              Live Rates
            </Button>
            <a
              href={`${MDD_SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(
                `Hi MyDailyDirect! Please share today's quality-tested wholesale rates for my shop (${buyerName}${buyerPhone ? `, Ph: ${buyerPhone}` : ""}).`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-control bg-tone-success-soft text-tone-success-text border border-tone-success-border text-xs font-bold hover:bg-tone-success-border/30 transition-colors"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              <span>WhatsApp Order</span>
            </a>
          </>
        }
      >
        Every batch is sourced from trusted, reliable mills and passes strict moisture, grading & quality tests.
      </Banner>

      {/* Commercial Kitchen Sample Testing Kit Banner (Hidden until packing facility is operational) */}
      {ENABLE_SAMPLE_KIT && (
        <div className="mb-2.5 p-3.5 sm:p-4 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300">
              <ChefHat className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-foreground text-xs sm:text-sm">
                  Tasting Sample Kit for Commercial Kitchens
                </span>
                <span className="text-[10px] font-black text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                  100% Free Trial
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed max-w-xl">
                Running a Restaurant, Darshini, Bakery or PG? Test grain fluffiness, cooking yield & frying quality in your kitchen before placing bulk orders.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setIsSampleKitModalOpen(true)}
            className="shrink-0 font-bold text-xs h-9 px-3.5 bg-amber-600 hover:bg-amber-700 text-white gap-1.5 shadow-xs"
          >
            <Package className="h-3.5 w-3.5" /> Request Tasting Kit
          </Button>
        </div>
      )}

      {/* 1-Tap Kitchen Reorder / "Buy Again" Staples Rail (Compact Micro-Rail) */}
      {repeatItems.length > 0 && (
        <div className="mb-2">
          <RepeatOrderRail
            items={repeatItems}
            onAddItem={(item) => cart.setQty(Number(item.id), cart.qtyOf(Number(item.id)) + item.qty)}
            onAddAll={(items) => {
              items.forEach((item) => {
                cart.setQty(Number(item.id), item.qty);
              });
              openCart();
            }}
          />
        </div>
      )}

      {/* Curated For You Focus Pack Banner */}
      <div className="mb-2.5 p-3 sm:p-3.5 rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-50/70 via-indigo-50/40 to-background dark:from-indigo-950/40 dark:via-indigo-950/20 dark:to-background flex items-center justify-between gap-3 shadow-2xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-xs">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-extrabold text-foreground text-xs sm:text-sm">
                Curated For You
              </span>
              <span className="text-[10px] font-black text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/60 px-2 py-0.2 rounded-full">
                Regular Focus Pack
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate">
              Order your routine staples directly with live 30-day low price alerts.
            </p>
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => navigate("/for-you")}
          className="shrink-0 font-bold text-xs h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white gap-1 shadow-xs"
        >
          <span>Open Pack</span>
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>

      {/* Search & Filter Header (scrolls naturally with page content, not sticky) */}
      <div className="border-b border-border pb-3 pt-1 space-y-2.5">
        {/* Row 1: Title + Sort */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-base sm:text-xl font-extrabold tracking-tight text-foreground whitespace-nowrap">
              <span className="hidden xs:inline sm:inline">Wholesale </span>Catalog
            </h1>
            <Badge variant="secondary" className="text-[11px] px-2 py-0.5 font-semibold shrink-0">
              {visible.length} SKUs
            </Badge>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <div className="flex items-center gap-1 bg-card border border-border rounded-lg px-2 py-1">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="border-none bg-transparent h-6 text-xs font-semibold focus:ring-0 py-0 pl-0.5 pr-4"
                aria-label="Sort products"
              >
                <option value="popular">Popular</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </Select>
            </div>
          </div>
        </div>

        {/* Row 2: Search Bar */}
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search rice, oil, dal, spices..."
          className="w-full"
        />

        {/* Row 3 (Mobile Only): Horizontal Scrollable Category Pills (Refactored to FilterChip) */}
        <div className="flex md:hidden items-center gap-1.5 overflow-x-auto no-scrollbar pt-0.5 pb-0.5 -mx-1 px-1">
          <button
            type="button"
            onClick={() => navigate("/for-you")}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-600 text-white shrink-0 shadow-xs hover:bg-indigo-700 transition-all"
          >
            <Sparkles className="h-3 w-3 text-amber-300" />
            <span>Curated For You</span>
          </button>
          {categories.map((cat) => (
            <FilterChip
              key={cat.value}
              label={cat.label}
              count={cat.count}
              icon={cat.icon}
              selected={selectedCategory === cat.value}
              onClick={() => setSelectedCategory(cat.value)}
            />
          ))}
        </div>
      </div>

      {/* Main Content Area: Desktop Sidebar + Full-Width Mobile Grid */}
      <div className="flex-1 flex pt-3 gap-5">
        {/* Desktop Left Category Sidebar (hidden on mobile so mobile gets 100% width) */}
        <aside className="hidden md:block w-52 shrink-0">
          <div className="sticky top-4 rounded-xl border border-border bg-card p-2 space-y-1">
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Categories
            </div>
            <button
              type="button"
              onClick={() => navigate("/for-you")}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all bg-indigo-600 text-white shadow-xs hover:bg-indigo-700 mb-1"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-300" />
                <span className="truncate">Curated For You</span>
              </div>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-black bg-white/20 text-white">
                Pack
              </span>
            </button>
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                    isSelected
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{cat.label}</span>
                  </div>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                      isSelected
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Product Grid */}
        <div className="flex-1 min-w-0">
          {skusQuery.isLoading && !propSkus ? (
            <div className="space-y-4">
              <InteractiveLoader
                compact
                title="Loading Quality-Tested Wholesale Catalog..."
                subtitle="Syncing live mill-direct commodity stock & daily wholesale rates"
              />
              <CatalogGridSkeleton count={8} />
            </div>
          ) : skusQuery.isError ? (
            <ErrorState
              description="Could not load wholesale catalog. Check your connection."
              action={{ label: "Retry", onClick: () => skusQuery.refetch() }}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="No matching products found"
              description={
                search || selectedCategory !== "ALL"
                  ? "Try clearing your search or selecting 'All Items'."
                  : "Products will appear here once added to the catalog."
              }
              action={
                search || selectedCategory !== "ALL"
                  ? {
                      label: "Reset Filters",
                      onClick: () => {
                        setSearch("");
                        setSelectedCategory("ALL");
                      },
                    }
                  : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visible.map((sku) => {
                const CategoryIcon = getCategoryIcon(sku.category ?? "");
                const totalSkuQty = sku.id === null ? 0 : cart.qtyOf(sku.id);
                return (
                  <ProductCard
                    key={sku.id}
                    sku={sku}
                    qty={totalSkuQty}
                    getQty={(packId) => (sku.id !== null ? cart.qtyOf(sku.id, packId) : 0)}
                    categoryIcon={CategoryIcon}
                    onSelect={() => sku.id !== null && navigate(`/product/${sku.id}`)}
                    onAdd={(packId) => sku.id !== null && cart.add(sku.id, packId)}
                    onIncrement={(packId) =>
                      sku.id !== null &&
                      cart.setQty(sku.id, cart.qtyOf(sku.id, packId) + 1, packId)
                    }
                    onDecrement={(packId) =>
                      sku.id !== null &&
                      cart.setQty(sku.id, cart.qtyOf(sku.id, packId) - 1, packId)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Floating Bottom Cart Summary Bar (Refactored to StickyActionBar primitive) */}
      <StickyActionBar
        totalItems={cart.totalItems}
        totalPaise={cartTotal}
        sublabel={`${cart.lines.length} ${cart.lines.length === 1 ? "item" : "items"} in cart`}
        onAction={openCart}
      />

      {/* Direct Shop Proof Upload Modal */}
      <UploadShopProofModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        retailer={retailer}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["retailers"] });
        }}
      />

      {/* Daily Mandi Bhav Rate Card Modal */}
      <MandiRateCardModal
        open={isRateCardOpen}
        onClose={() => setIsRateCardOpen(false)}
        skus={skusQuery.data ?? []}
      />

      {/* Commercial Kitchen Tasting Sample Kit Modal (Hidden until packing facility is operational) */}
      {ENABLE_SAMPLE_KIT && (
        <SampleKitModal
          open={isSampleKitModalOpen}
          onClose={() => setIsSampleKitModalOpen(false)}
          defaultBusinessName={buyerName || ""}
          defaultPhone={buyerPhone || ""}
          defaultGstNumber={retailer?.gstNumber || ""}
          defaultDocUrl={retailer?.photoUrl || ""}
          isVerifiedBuyer={Boolean(isApproved && hasGstNumber && hasShopProof)}
          onRequestSubmitted={async (data) => {
            await client.sampleRequests.create(data);
            if (retailer?.id && (!retailer.gstNumber || !retailer.photoUrl) && (data.gstNumber || data.docUrl)) {
              try {
                await client.retailers.update(retailer.id, {
                  ...retailer,
                  gstNumber: data.gstNumber || retailer.gstNumber,
                  photoUrl: data.docUrl || retailer.photoUrl,
                });
                queryClient.invalidateQueries({ queryKey: ["retailers"] });
              } catch {
                // Non-blocking
              }
            }
          }}
        />
      )}
    </div>
  );
}

